import { ImageCropModal } from '@/components/cropper/ImageCropModal';
import { Form, useForm } from '@/components/forms';
import Input from '@/components/forms/Input';
import { Upload } from '@/components/upload';
import { nameValidationRegex } from '@/constants/regex';
import { useAddStoreGallery } from '@/features/store-management/premium-stores/hooks/useAddStoreGallery';
import { useUpdateStoreGallery } from '@/features/store-management/premium-stores/hooks/useUpdateStoreGallery';
import { Translation, useDynamicLocale } from '@/hoslog-intl';
import { optionalUploadValueSchema } from '@/lib/uploadSchema';
import { FileObject, uploadFileToS3WithPresignedUrl } from '@/lib/uploadToAWS';
import { useEffect, useState } from 'react';
import { Controller } from 'react-hook-form';
import { z } from 'zod';

const addHostGalleryFormSchema = z
  .object({
    imageTitle: z.string().min(1, { message: 'common.form.validation.required' }).regex(nameValidationRegex, {
      message: 'common.form.validation.nameOnly',
    }),
    existingImageSrc: z.string().optional().default(''),
    imagePath: optionalUploadValueSchema,
  })
  .superRefine((val, ctx) => {
    const hasExisting = !!val.existingImageSrc;
    const hasNewFile = !!val.imagePath && typeof val.imagePath === 'object';
    if (!hasExisting && !hasNewFile) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['imagePath'],
        message: 'common.form.validation.required',
      });
    }
  });

type Props = {
  clubId: string;
  hostId: string;
  galleryId?: string;
  defaultValues?: {
    imageTitle: string;
    imageSrc: string;
  };
};

export const AddHostGalleryForm = ({ clubId, hostId, galleryId = '', defaultValues }: Props) => {
  const form = useForm({
    schema: addHostGalleryFormSchema,
    mode: 'onBlur',
    defaultValues: {
      imageTitle: defaultValues?.imageTitle ?? '',
      existingImageSrc: defaultValues?.imageSrc ?? '',
      imagePath: '',
    },
  });

  const { mutateAsync: mutateHostGalleryCreation } = useAddStoreGallery();
  const { mutateAsync: mutateHostGalleryUpdate } = useUpdateStoreGallery();
  const { t } = useDynamicLocale();
  const [apiUrl, setApiUrl] = useState(defaultValues?.imageSrc ?? '');
  const [cropSource, setCropSource] = useState<string | null>(null);
  const [isCropOpen, setIsCropOpen] = useState(false);
  const [uploadKey, setUploadKey] = useState(0);

  useEffect(() => {
    setApiUrl(defaultValues?.imageSrc ?? '');
  }, [defaultValues?.imageSrc]);

  useEffect(() => {
    return () => {
      if (cropSource?.startsWith('blob:')) {
        URL.revokeObjectURL(cropSource);
      }
    };
  }, [cropSource]);

  return (
    <>
      <Form
        form={form}
        onSubmit={async () => {
          const imagePath = form.getValues('imagePath');
          const existing = form.getValues('existingImageSrc');
          const imageTitle = form.getValues('imageTitle');

          const hasNewFile = imagePath && typeof imagePath === 'object';

          let imageUrl = existing;
          if (hasNewFile) {
            imageUrl = await uploadFileToS3WithPresignedUrl(imagePath as FileObject);
          }

          if (!galleryId) {
            await mutateHostGalleryCreation({ clubId, hostId, imagePath: imageUrl || '', imageTitle });
          } else {
            await mutateHostGalleryUpdate({ clubId, hostId, galleryId, imagePath: imageUrl || '', imageTitle });
          }
        }}
      >
        <div className="w-full flex flex-col gap-4.5">
          <Input
            forceRequired
            label="storeManagement.galleryManagement.page.addGallery.title"
            placeholder={t('storeManagement.basicStores.galleryManagement.placeholder.enterTitle')}
            {...form.register('imageTitle')}
            maxLength={50}
          />
          <Controller
            name="imagePath"
            control={form.control}
            render={({ field, fieldState }) => (
              <Upload
                isRequired
                label="storeManagement.galleryManagement.page.addGallery.uploadImage"
                maxSizeMB={5}
                accept=".jpg,.jpeg,.png,.svg,.heic,.heif"
                dropZoneClassName="w-full bg-surface-subtle"
                buttonText={t('common.page.button.browseFiles')}
                multiple={false}
                key={uploadKey}
                defaultPreviewUrl={apiUrl}
                previewClassName="w-full object-contain h-[380px]"
                error={fieldState.error?.message}
                onFilesChange={(files) => {
                  const selected = files?.[0];

                  if (!selected || typeof selected === 'string') {
                    field.onChange('');
                    return;
                  }

                  const rawFile = selected.file as File;
                  const localPreview = selected.preview || URL.createObjectURL(rawFile);

                  setCropSource(localPreview);
                  setIsCropOpen(true);
                }}
                onDefaultPreviewRemove={() => {
                  setApiUrl('');
                  form.setValue('existingImageSrc', '', { shouldValidate: true, shouldDirty: true });
                  form.setValue('imagePath', '', { shouldValidate: true, shouldDirty: true });
                  field.onChange('');
                }}
              >
                <div className="text-muted-foreground text-caption font-medium">
                  <Translation id="storeManagement.basicStores.galleryManagement.label.imageNote" />
                </div>
              </Upload>
            )}
          />
        </div>
      </Form>
      <ImageCropModal
        open={isCropOpen}
        imageSrc={cropSource}
        type="host"
        onClose={() => {
          setIsCropOpen(false);

          if (cropSource?.startsWith('blob:')) {
            URL.revokeObjectURL(cropSource);
          }
          setCropSource(null);
          setUploadKey((k) => k + 1);

          // Restore to the previous valid state
          setApiUrl(defaultValues?.imageSrc ?? '');
          form.setValue('existingImageSrc', defaultValues?.imageSrc ?? '', { shouldValidate: true });
          form.setValue('imagePath', '', { shouldValidate: true });
        }}
        onCropDone={(croppedFile, previewUrl) => {
          const croppedFileObject: FileObject = {
            file: croppedFile,
            id: `${Date.now()}`,
            preview: previewUrl,
          };

          if (cropSource?.startsWith('blob:')) {
            URL.revokeObjectURL(cropSource);
          }

          setApiUrl(previewUrl);
          form.setValue('imagePath', croppedFileObject, { shouldValidate: true, shouldDirty: true });
          form.setValue('existingImageSrc', '', { shouldValidate: true, shouldDirty: true });

          setCropSource(null);
          setIsCropOpen(false);
          setUploadKey((k) => k + 1);
        }}
      />
    </>
  );
};
